import { useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye } from 'lucide-react';
import { Button, Dialog, Field, Input } from '@/components/ui';
import { useObserveSite } from '@/hooks/useSites';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import { splitList } from '@/utils/validation';

/**
 * Watching a site the panel does not run (S7): an existing site on its own
 * server, before it moves here or instead of moving. It is checked from
 * outside and raises alerts like any other; nothing is ever done to it.
 */
export function ObserveSiteDialog({ open, onClose }: { readonly open: boolean; readonly onClose: () => void }): ReactNode {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [aliases, setAliases] = useState('');

  const observe = useObserveSite({
    onSuccess: ({ siteId }) => {
      toast.success('Сайт добавлен в мониторинг');
      close();
      navigate(`/sites/${siteId}`);
    },
  });

  function close(): void {
    if (observe.isPending) return;
    setName('');
    setDomain('');
    setAliases('');
    observe.reset();
    onClose();
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (name.trim() && domain.trim()) {
      observe.mutate({ displayName: name.trim(), primaryDomain: domain.trim().toLowerCase(), aliases: splitList(aliases) });
    }
  }

  return (
    <Dialog
      open={open}
      title="Мониторинг внешнего сайта"
      description="Проверки доступности и SEO без управления сайтом"
      onClose={close}
      size="sm"
      footer={
        <>
          <Button onClick={close} disabled={observe.isPending}>
            Отмена
          </Button>
          <Button variant="primary" icon={Eye} busy={observe.isPending} type="submit" form="observe-site" disabled={!name.trim() || !domain.trim()}>
            Добавить
          </Button>
        </>
      }
    >
      <form id="observe-site" className="stack" onSubmit={submit}>
        <Field label="Название" htmlFor="observe-name">
          <Input id="observe-name" value={name} onChange={(event) => setName(event.target.value)} placeholder="Новости Кореи" autoComplete="off" />
        </Field>
        <Field label="Основной домен" htmlFor="observe-domain" hint="Главная страница проверяется по HTTPS">
          <Input id="observe-domain" value={domain} onChange={(event) => setDomain(event.target.value)} placeholder="example.com" autoComplete="off" />
        </Field>
        <Field label="Другие домены" htmlFor="observe-aliases" hint="Через пробел или запятую, например www-версия">
          <Input id="observe-aliases" value={aliases} onChange={(event) => setAliases(event.target.value)} placeholder="www.example.com" autoComplete="off" />
        </Field>
        {observe.isError ? (
          <p className="field-error" role="alert">
            {errorText(observe.error)}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
